import { DayOfWeek, FlexRange, TimeRangeInput } from "@clockwise/schema/v2";
import {
  EventTable,
  EventTableRow,
  EventTableRowCollapser,
} from "@clockwise/web-commons/src/components/EventTableNew";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { DateTime, Interval } from "luxon";
import React from "react";
import { MyFlexibleMeetingsListDocument } from "./__generated__/MyFlexibleMeetings.v2.generated";
import { Async, FlexEventRow } from "./flex-event-table/FlexEventRow";
import { EventFlexMeta } from "./flex-event-table/FlexSettingsModalWrapper";
import { useSetFlexibility } from "./useSetFlexibility";

const trackingSourceId = "My flexible meetings control";

type TimeRangeFlexResponse =
  | {
      __typename: "TimeRange";
      start: string | null;
      end: string | null;
    }
  | null
  | undefined;

export const getTimeRangeFromFlex = (timeRange: TimeRangeFlexResponse): TimeRangeInput | null => {
  if (timeRange?.start && timeRange?.end) {
    return {
      start: timeRange?.start,
      end: timeRange?.end,
    };
  } else {
    return null;
  }
};

export const MyFlexibleMeetings = ({
  onOpenSettings,
}: {
  onOpenSettings: (event: EventFlexMeta) => void;
}) => {
  const { data, loading, error } = useGatewayQuery(MyFlexibleMeetingsListDocument);
  const { handleFlexibilityChange } = useSetFlexibility(
    [
      ...(data?.flexibleMeetingsDashboardV2?.others || []),
      ...(data?.flexibleMeetingsDashboardV2?.teamSyncs || []),
      ...(data?.flexibleMeetingsDashboardV2?.upcomingAdHoc || []),
    ],
    trackingSourceId,
  );

  const onOpenSettingsModal = (meeting: {
    id: string;
    flexible: boolean;
    flexRange: FlexRange;
    startTime: DateTime;
    timeOfDayFlexRange: TimeRangeInput | null;
    allowedDays: DayOfWeek[];
  }) => {
    onOpenSettings({
      eventId: meeting.id,
      startTime: meeting.startTime,
      flexDetails: {
        allowedDays: meeting.allowedDays,
        flexRange: meeting.flexRange,
        isFlexible: meeting.flexible,
        timeOfDayRange: meeting.timeOfDayFlexRange,
      },
    });
  };

  const otherMeetings = (data?.flexibleMeetingsDashboardV2.others ?? []).map((meeting) => ({
    id: meeting.externalEventId,
    attendees: meeting.attendees.map((a) => a.person),
    flexible: !!meeting.flexibility?.isFlexible,
    flexibilityEditable: meeting.canMarkFlexible,
    startTime: Interval.fromISO(
      meeting.dateOrTimeRange.__typename === "DateRange"
        ? meeting.dateOrTimeRange.dateRange
        : meeting.dateOrTimeRange.timeRange,
    ).start,
    allowedDays: meeting.flexibility?.allowedDays || [],
    timeOfDayFlexRange: getTimeRangeFromFlex(meeting.flexibility?.timeOfDayFlexRange),
    flexRange: meeting.flexibility?.flexRange || FlexRange.Day,
    recurrenceType: meeting.recurrence?.type,
    title: meeting.title,
  }));

  const teamSyncs = (data?.flexibleMeetingsDashboardV2.teamSyncs ?? []).map((meeting) => ({
    id: meeting.externalEventId,
    attendees: meeting.attendees.map((a) => a.person),
    flexible: !!meeting.flexibility?.isFlexible,
    flexibilityEditable: meeting.canMarkFlexible,
    startTime: Interval.fromISO(
      meeting.dateOrTimeRange.__typename === "DateRange"
        ? meeting.dateOrTimeRange.dateRange
        : meeting.dateOrTimeRange.timeRange,
    ).start,
    allowedDays: meeting.flexibility?.allowedDays || [],
    timeOfDayFlexRange: getTimeRangeFromFlex(meeting.flexibility?.timeOfDayFlexRange),
    flexRange: meeting.flexibility?.flexRange || FlexRange.Day,
    recurrenceType: meeting.recurrence?.type,
    title: meeting.title,
  }));
  const adHoc = (data?.flexibleMeetingsDashboardV2.upcomingAdHoc ?? []).map((meeting) => ({
    id: meeting.externalEventId,
    attendees: meeting.attendees.map((a) => a.person),
    flexible: !!meeting.flexibility?.isFlexible,
    flexibilityEditable: meeting.canMarkFlexible,
    startTime: Interval.fromISO(
      meeting.dateOrTimeRange.__typename === "DateRange"
        ? meeting.dateOrTimeRange.dateRange
        : meeting.dateOrTimeRange.timeRange,
    ).start,
    allowedDays: meeting.flexibility?.allowedDays || [],
    timeOfDayFlexRange: getTimeRangeFromFlex(meeting.flexibility?.timeOfDayFlexRange),
    flexRange: meeting.flexibility?.flexRange || FlexRange.Day,
    recurrenceType: meeting.recurrence?.type,
    title: meeting.title,
  }));

  return (
    <EventTable
      heading="Other meetings"
      errorMessage={
        error ? "There was an error fetching your meetings. Please try again." : undefined
      }
      emptyMessage={""}
    >
      <EventTableRow isHeading={true}>
        <>Meeting</>
        <>Attendees</>
        <>Repeats</>
        <>Flexibility</>
      </EventTableRow>
      <Async loading={loading} loadingTypes={["text", "avatar", "text", "text"]}>
        <EventTableRowCollapser text="Upcoming ad hoc">
          {adHoc.map((meeting) => {
            const { id, title, flexible, flexRange, recurrenceType, attendees } = meeting;
            return (
              <FlexEventRow
                eventId={id}
                flexible={flexible}
                flexRange={flexRange || FlexRange.Day}
                key={id}
                attendees={attendees}
                recurrence={recurrenceType}
                title={title}
                showAttendees
                onFlexibilityChange={handleFlexibilityChange}
                openFlexSettings={() => onOpenSettingsModal(meeting)}
              />
            );
          })}
        </EventTableRowCollapser>
      </Async>
      <Async loading={loading} loadingTypes={["text", "avatar", "text", "text"]}>
        <EventTableRowCollapser text="Team syncs">
          {teamSyncs.map((meeting) => {
            const { id, title, flexible, flexRange, recurrenceType, attendees } = meeting;
            return (
              <FlexEventRow
                eventId={id}
                flexible={flexible}
                flexRange={flexRange || FlexRange.Day}
                key={id}
                attendees={attendees}
                recurrence={recurrenceType}
                title={title}
                showAttendees
                onFlexibilityChange={handleFlexibilityChange}
                openFlexSettings={() => onOpenSettingsModal(meeting)}
              />
            );
          })}
        </EventTableRowCollapser>
      </Async>
      <Async loading={loading} loadingTypes={["text", "avatar", "text", "text"]}>
        <EventTableRowCollapser text="Other">
          {otherMeetings.map((meeting) => {
            const { id, title, flexible, flexRange, recurrenceType, attendees } = meeting;
            return (
              <FlexEventRow
                eventId={id}
                flexible={flexible}
                attendees={attendees}
                flexRange={flexRange || FlexRange.Day}
                key={id}
                recurrence={recurrenceType}
                title={title}
                showAttendees
                onFlexibilityChange={handleFlexibilityChange}
                openFlexSettings={() => onOpenSettingsModal(meeting)}
              />
            );
          })}
        </EventTableRowCollapser>
      </Async>
    </EventTable>
  );
};
